import React, { useState, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Select from "react-select";
import { api } from "../shared/axiosInstance";
import JobPostsList from "./JobPostsList";
import JobPostModal from "./JobPostModal";
import CompanyUsers from "./CompanyUsers";
import CreditTransactions from "./CreditTransactions";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { useCompany } from "../../context/CompanyContext";
import PurchaseCreditsModal from "./PurchaseCreditsModal";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from '../shared/stripe';
import selectStyles from '../shared/SelectedStyles';
import OnboardingWizardModal from "./OnboardingWizardModal";
import NonAdminWelcomeBanner from "./NonAdminWelcomeBanner";
import PendingApprovalBanner from "./PendingApprovalBanner";

const EmployerDashboardContent = () => {
    // Declare all hooks unconditionally
    const [jobPosts, setJobPosts] = useState([]);
    const [users, setUsers] = useState([]);
    const [activeTab, setActiveTab] = useState("jobs");
    const [, setIsPaymentModalOpen] = useState(false);
    const [, setSelectedJob] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [titleFilter, setTitleFilter] = useState("");
    const [selectedStatuses, setSelectedStatuses] = useState([{ value: "Open", label: "Open" }]);
    const [targetNameFilter, setTargetNameFilter] = useState("");
    const [targetEmailFilter, setTargetEmailFilter] = useState("");
    const { companyCredits, companyName, setCompanyData } = useCompany();
    const [userId, setUserId] = useState("");
    const [creatorOptions, setCreatorOptions] = useState([]);
    const [selectedCreators, setSelectedCreators] = useState([]);
    const [modalContent, setModalContent] = useState(null);
    const [referralStats, setReferralStats] = useState(null);
    const [showReferralStats, setShowReferralStats] = useState(false);
    const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(null);
    const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
    const [wizardStep, setWizardStep] = useState(0);
    const [wizardTriggeredJob, setWizardTriggeredJob] = useState(false);
    const [pendingApprovals, setPendingApprovals] = useState([]);
    const [loadingApprovals, setLoadingApprovals] = useState(new Set());
    const [searchParams] = useSearchParams();

    const handleUnlockJob = (job) => {
        setSelectedJob(job);
        setIsPaymentModalOpen(true);
    };

    useEffect(() => {
        const fetchCreators = async () => {
            const response = await api.get("/jobs/creators");
            const options = response.data.map((user) => ({
                value: user._id,
                label: `${user.firstName} ${user.lastName}`,
            }));

            setCreatorOptions(options);

            // Default to showing all creators so users see all company posts
            setSelectedCreators(options);
        };

        if (userId) {
            fetchCreators();
        }
    }, [userId]);


    const statusOptions = [
        { value: "Open", label: "Open" },
        { value: "Filled", label: "Filled" },
        { value: "Canceled", label: "Canceled" }
    ];
    const referralLink = userId ? `https://careermission.ai/candidate-signup?referredBy=${userId}` : "";

    // Determine user data from impersonation or token
    let decoded = null;
    const impersonatedUser = localStorage.getItem("impersonatedUser");
    if (impersonatedUser) {
        try {
            decoded = JSON.parse(impersonatedUser);
        } catch (err) {
            console.error("Error parsing impersonated user:", err);
        }
    } else {
        const token = localStorage.getItem("token");
        if (token) {
            try {
                decoded = jwtDecode(token);
            } catch (err) {
                console.error("Error decoding token:", err);
            }
        }
    }
    // Use case-insensitive check
    const userRole = decoded ? (decoded.role || "").toLowerCase() : "";
    const companyApprovalStatus = decoded ? (decoded.companyApprovalStatus || 'approved') : 'approved';
    const shouldRedirect = userRole === "candidate";
    const fetchCompanyDetails = async (companyId) => {
        try {
            const token = localStorage.getItem("token");
            const response = await api.get(`/company/details?companyId=${companyId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCompanyData({
                name: response.data.name,
                credits: response.data.credits || 0,
            });
        } catch (error) {
            console.error("❌ Failed to fetch company details:", error.response?.data || error);
        }
    };
    useEffect(() => {
        const stored = localStorage.getItem("impersonatedUser");
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setUserId(parsed._id);
                if (parsed.company) {
                    fetchCompanyDetails(parsed.company);
                }
            } catch (err) {
                console.error("Error parsing impersonated user:", err);
            }
        } else {
            const token = localStorage.getItem("token");
            if (token) {
                try {
                    const decoded = jwtDecode(token);
                    setUserId(decoded.userId);
                    if (decoded.company) {
                        fetchCompanyDetails(decoded.company);
                    }
                } catch (err) {
                    console.error("Error decoding token:", err);
                }
            }
        }
    }, [fetchCompanyDetails]);


    useEffect(() => {
        fetchJobPosts();
        fetchCompanyUsers();
        fetchReferralStats();
        fetchOnboardingStatus();
    }, []);

    const fetchOnboardingStatus = async () => {
        try {
            const res = await api.get("/auth/user/me");
            const completed = res.data.hasCompletedOnboarding;
            setHasCompletedOnboarding(completed);
            if (!completed) {
                setShowOnboardingWizard(true);
            }
        } catch (err) {
            console.error("Failed to fetch onboarding status:", err);
        }
    };

    const fetchReferralStats = async () => {
        try {
            const response = await api.get("/auth/referral-stats");
            setReferralStats(response.data);
        } catch (error) {
            console.error("❌ Failed to fetch referral stats:", error.response?.data || error);
        }
    };

    const fetchJobPosts = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await api.get("/jobs", {
                params: { impersonatedUserId: decoded?._id },
                headers: { Authorization: `Bearer ${token}` }
            });
            setJobPosts(response.data);
        } catch (error) {
            console.error("❌ Failed to load job posts:", error.response?.data || error);
        }
    };

    const fetchCompanyUsers = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await api.get("/company/users", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data);
        } catch (error) {
            console.error("❌ Failed to load company users:", error.response?.data || error);
        }
    };

    useEffect(() => {
        if (userRole === 'company admin' && companyApprovalStatus === 'approved') {
            api.get('/company/pending-approvals')
                .then(res => setPendingApprovals(res.data))
                .catch(() => {});
        }
    }, [userRole, companyApprovalStatus]);

    const handleApproval = async (userId, action) => {
        setLoadingApprovals(prev => new Set(prev).add(userId));
        try {
            await api.post(`/company/${action}-member`, { userId });
            setPendingApprovals(prev => prev.filter(u => u._id !== userId));
        } catch (err) {
            console.error('Approval action failed:', err);
        } finally {
            setLoadingApprovals(prev => {
                const next = new Set(prev);
                next.delete(userId);
                return next;
            });
        }
    };

    const creatorIds = selectedCreators.map((c) => c.value);

    const filteredJobs = jobPosts.filter((job) => {
        const creatorMatch = creatorIds.length ? creatorIds.includes(job.createdBy) : true;
        const titleMatch = (job.title || "").toLowerCase().includes((titleFilter || "").toLowerCase());
        const selectedStatusValues = selectedStatuses.map((s) => s.value);
        const statusMatch = selectedStatusValues.includes(job.status);
        const targetNameMatch = targetNameFilter
            ? job.targets?.some((target) =>
                (`${target.firstName || ""} ${target.lastName || ""}`).toLowerCase().includes(targetNameFilter.toLowerCase())
            )
            : true;
        const targetEmailMatch = targetEmailFilter
            ? job.targets?.some((target) => (target.email || "").toLowerCase().includes(targetEmailFilter.toLowerCase()))
            : true;

        return creatorMatch && titleMatch && statusMatch && targetNameMatch && targetEmailMatch;
    });


    useEffect(() => {
        if (searchParams.get("showOnboarding") === "true") {
            setShowOnboardingWizard(true);
        }
    }, [searchParams]);

    const handleOnboardingComplete = async () => {
        try {
            await api.patch("/auth/user/complete-onboarding");
            setHasCompletedOnboarding(true);
            setShowOnboardingWizard(false);
        } catch (err) {
            console.error("Failed to mark onboarding complete:", err);
            setShowOnboardingWizard(false);
        }
    };

    const copyToClipboard = () => {
        if (!referralLink) return;
        navigator.clipboard.writeText(referralLink);
        alert("Referral link copied!");
    };

    return shouldRedirect ? (
        <Navigate to="/profile" />
    ) : (
        <div className="px-6 pt-8 pb-8 bg-darkblue text-white">
            {userRole === "company admin" && (
                <OnboardingWizardModal
                    isOpen={showOnboardingWizard}
                    onClose={() => setShowOnboardingWizard(false)}
                    onComplete={handleOnboardingComplete}
                    onPostJob={() => {
                        setWizardTriggeredJob(true);
                        setShowOnboardingWizard(false);
                        setIsModalOpen(true);
                    }}
                    onNavigateToUsersTab={() => {
                        setActiveTab("users");
                        setShowOnboardingWizard(false);
                    }}
                    userId={userId}
                    companyName={companyName}
                    initialStep={wizardStep}
                />
            )}
            {userRole === "recruiter" && hasCompletedOnboarding === false && (
                <NonAdminWelcomeBanner
                    companyName={companyName}
                    onDismiss={handleOnboardingComplete}
                />
            )}
            {companyApprovalStatus === 'pending' ? (
                <PendingApprovalBanner companyName={companyName || 'your company'} />
            ) : (
            <>
        <h1 className="text-3xl font-bold text-center mb-6">{companyName} Employer Dashboard</h1>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                <p className="text-lightgray m-0 text-center">
                    Current Credits: <span className="text-orange font-bold">{companyCredits}</span>
                </p>
                <button
                    onClick={() => setModalContent({ type: "addCredits" })}
                    className="bg-orange hover:bg-deeporange text-lightgray py-1 px-3 rounded"
                >
                    Add Credits
                </button>
            </div>

            {referralLink && (
                <div className="flex flex-col items-center justify-center bg-gray-800 p-4 rounded-lg shadow-md mb-4 space-y-4">
                    {/* Referral Link */}
                    <div className="flex flex-col items-center">
                        <span className="text-lightblue font-semibold mb-1">General Referral Link</span>
                        <p className="text-xs text-gray-400 mb-2 text-center max-w-lg">Share this anywhere to invite candidates to Career Mission. For job-specific links that auto-match candidates to a role, use the link on each job post below.</p>
                        <div className="flex items-center">
                            <input
                                type="text"
                                value={referralLink}
                                readOnly
                                className="bg-gray-700 text-white p-2 rounded-lg border border-gray-500 w-96"
                            />
                            <button onClick={copyToClipboard} aria-label="Copy referral link to clipboard" className="ml-2 p-2 bg-lightblue rounded-lg hover:bg-orange">
                                <ClipboardDocumentIcon className="h-6 w-6 text-darkblue" />
                            </button>
                        </div>
                    </div>

                    {/* Fold/Unfold for Referral Stats */}
                    {referralStats && (
                        <div className="w-full flex flex-col items-center">
                            <button
                                onClick={() => setShowReferralStats(prev => !prev)}
                                className="text-lightblue hover:text-orange text-sm flex items-center gap-1"
                            >
                                {showReferralStats ? "Hide Referral Stats ▲" : "Show Referral Stats ▼"}
                            </button>

                            {showReferralStats && (
                                <div className="mt-2 text-center">
                                    <div className="text-lightblue font-semibold mb-2">Referral Stats</div>
                                    <p className="text-lightgray">
                                        You: <span className="text-orange font-bold">{referralStats.personalSignups}</span> signed up,
                                        <span className="text-orange font-bold"> {referralStats.personalCompleted}</span> completed
                                    </p>
                                    <p className="text-lightgray">
                                        Whole Company: <span className="text-orange font-bold">{referralStats.companySignups}</span> signed up,
                                        <span className="text-orange font-bold"> {referralStats.companyCompleted}</span> completed
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {userRole === 'company admin' && pendingApprovals.length > 0 && (
                <div className="bg-bluegray rounded-lg p-4 mb-4">
                    <h3 className="text-lg font-semibold text-lightblue mb-3">Pending Team Member Requests</h3>
                    {pendingApprovals.map(user => (
                        <div key={user._id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                            <div>
                                <span className="text-white">{user.firstName} {user.lastName}</span>
                                <span className="text-lightgray text-sm ml-2">({user.email})</span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleApproval(user._id, 'approve')}
                                    disabled={loadingApprovals.has(user._id)}
                                    className={`px-3 py-1 text-white text-sm rounded ${loadingApprovals.has(user._id) ? 'bg-green-600 opacity-50 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                                >
                                    Approve
                                </button>
                                <button
                                    onClick={() => handleApproval(user._id, 'deny')}
                                    disabled={loadingApprovals.has(user._id)}
                                    className={`px-3 py-1 text-white text-sm rounded ${loadingApprovals.has(user._id) ? 'bg-red-600 opacity-50 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                                >
                                    Deny
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tab Navigation */}
            <div className="flex border-b border-lightblue mb-4">
                <button
                    className={`px-4 py-2 ${activeTab === "jobs" ? "border-b-2 border-orange" : "text-gray-400"}`}
                    onClick={() => setActiveTab("jobs")}
                >
                    Job Posts
                </button>
                <button
                    className={`px-4 py-2 ml-4 ${activeTab === "users" ? "border-b-2 border-orange" : "text-gray-400"}`}
                    onClick={() => setActiveTab("users")}
                >
                    Manage Users
                </button>
                {/* NEW: Credits Tab */}
                <button
                    className={`px-4 py-2 ml-4 ${activeTab === "credits" ? "border-b-2 border-orange" : "text-gray-400"}`}
                    onClick={() => setActiveTab("credits")}
                >
                    Credits
                </button>
            </div>
            {/* Content */}
            {activeTab === "jobs" ? (
                <>
                    <div className="bg-gray-800 p-4 rounded-lg shadow-md mb-4 flex flex-wrap gap-6">
                        <div className="flex flex-col w-64">
                            <label className="text-lightgray mb-1">Filter by Job Creator</label>
                            <Select
                                options={creatorOptions}
                                isMulti
                                value={selectedCreators}
                                onChange={setSelectedCreators}
                                styles={selectStyles}
                                classNamePrefix="select"
                                placeholder="Select Job Creators"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-lightgray mb-1">Filter by Job Title</label>
                            <input
                                type="text"
                                placeholder="Enter Job Title"
                                value={titleFilter}
                                onChange={(e) => setTitleFilter(e.target.value)}
                                className="p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                            />
                        </div>
                        <div className="flex flex-col w-64">
                            <label className="text-lightgray mb-1">Filter by Status</label>
                            <Select
                                options={statusOptions}
                                isMulti
                                value={selectedStatuses}
                                onChange={setSelectedStatuses}
                                styles={selectStyles}
                                classNamePrefix="select"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-lightgray mb-1">Filter by Target Name</label>
                            <input
                                type="text"
                                placeholder="Enter Target Name"
                                value={targetNameFilter}
                                onChange={(e) => setTargetNameFilter(e.target.value)}
                                className="p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-lightgray mb-1">Filter by Target Email</label>
                            <input
                                type="text"
                                placeholder="Enter Target Email"
                                value={targetEmailFilter}
                                autoComplete="new-password"
                                onChange={(e) => setTargetEmailFilter(e.target.value)}
                                className="p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                            />
                        </div>
                    </div>
                    <div className="bg-bluegray p-4 rounded-lg shadow-md">
                        <JobPostsList
                            jobPosts={filteredJobs}
                            totalJobCount={jobPosts.length}
                            refreshJobPosts={fetchJobPosts}
                            onUnlockJob={handleUnlockJob}
                            userId={userId}
                            onShowWizard={() => setShowOnboardingWizard(true)}
                        />
                    </div>
                </>
            ) : activeTab === "users" ? (
                <div className="bg-bluegray p-4 rounded-lg shadow-md">
                    <CompanyUsers users={users} refreshUsers={fetchCompanyUsers} />
                </div>
            ) : activeTab === "credits" ? (
                <>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-orange text-lg font-bold">Credit Transactions</h2>
                        <button
                            onClick={() => setModalContent({ type: "addCredits" })}
                            className="bg-orange hover:bg-deeporange text-lightgray py-1 px-3 rounded"
                        >
                            Add Credits
                        </button>
                    </div>
                    <CreditTransactions />
                </>
            ) : null}
            </>
            )}
            {modalContent?.type === "addCredits" && (
                <Elements stripe={stripePromise}>
                    <PurchaseCreditsModal
                        onClose={() => {
                            fetchCompanyDetails(decoded.company);
                            setModalContent(null);
                        }}
                    />
                </Elements>
            )}
            <JobPostModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    if (wizardTriggeredJob) {
                        setWizardTriggeredJob(false);
                        setWizardStep(1);
                        setShowOnboardingWizard(true);
                    }
                }}
                onSave={() => {
                    fetchJobPosts();
                    if (wizardTriggeredJob) {
                        setWizardTriggeredJob(false);
                        setIsModalOpen(false);
                        setWizardStep(1);
                        setShowOnboardingWizard(true);
                    }
                }}
            />
        </div>
    );
};

export default EmployerDashboardContent;
